import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  GlobalStyles,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DockIcon from '@mui/icons-material/Dock';
import { useTranslation } from 'react-i18next';
import PriorResultsCard from './PriorResultsCard';
import AISummaryCard from './AISummaryCard';
import usePriorStudies from './usePriorStudies';
import {
  useReportSidebarPopupWindow,
  useReportSidebarContextListener,
} from './useReportSidebarPopup';
import { postReportSidebarMessage, SYNC_MESSAGE } from '../../../utils/reportSidebarSync';
import { openInViewerWindow } from '../../../utils/viewerWindow';
import { createApiUrl } from '../../../utils/apiConfig';
import { getAuthHeaders } from '../../../utils/authUtils';
import { useTheme } from '@mui/material/styles';
import { useSnackbar } from '../../CentralSnackbar';

const popupGlobalStyles = (
  <GlobalStyles
    styles={{
      'html, body, #root': {
        width: '100%',
        height: '100%',
        margin: 0,
        padding: 0,
        overflow: 'hidden',
      },
    }}
  />
);

export default function ReportSidebarPopup() {
  const { t } = useTranslation();
  const theme = useTheme();
  const { showError } = useSnackbar();
  const [syncContext, setSyncContext] = useState(null);
  const [aiSummaryExpanded, setAiSummaryExpanded] = useState(true);
  const [viewerConfig, setViewerConfig] = useState({ web: {}, client: {} });
  const [currentUser, setCurrentUser] = useState(null);

  const priorStudies = usePriorStudies(syncContext?.patientId);

  useReportSidebarContextListener(setSyncContext, () => setSyncContext(null));

  useReportSidebarPopupWindow();

  useEffect(() => {
    const loadConfig = async () => {
      try {
        const [userRes, viewerRes] = await Promise.all([
          fetch(createApiUrl('auth/me'), { headers: getAuthHeaders() }),
          fetch(createApiUrl('profile/viewer-config'), { headers: getAuthHeaders() }),
        ]);
        if (userRes.ok) setCurrentUser(await userRes.json());
        if (viewerRes.ok) setViewerConfig(await viewerRes.json());
      } catch (err) {
        console.error('[ReportSidebarPopup] config load failed:', err);
      }
    };
    loadConfig();
  }, []);

  const openStudyDirectly = useCallback((study, userViewerType) => {
    const viewerSetting = viewerConfig[userViewerType];
    if (!viewerSetting?.base_url) {
      showError(t('approval.error.noViewerURL', { viewerType: userViewerType }), 'ERR-CONFIG-003');
      return;
    }
    const viewerUrl = viewerSetting.base_url.replace('[studyInstanceUID]', study.study_instance_uid);
    if (userViewerType === 'web') {
      openInViewerWindow(viewerUrl);
    } else if (userViewerType === 'client') {
      window.location.href = viewerUrl;
    }
  }, [viewerConfig, showError, t]);

  const handleOpenInViewer = useCallback((study) => {
    if (!study?.study_instance_uid) {
      showError(t('approval.error.noStudyUID'), 'ERR-PACS-002');
      return;
    }
    if (!currentUser?.viewer_preference) {
      showError(t('approval.error.viewerSettingUnavailable'), 'ERR-CONFIG-004');
      return;
    }
    const studyModality = (study.modality || '').toUpperCase();
    const forceWebViewer = studyModality.includes('ECG')
      || studyModality.includes('EKG')
      || studyModality === 'OT'
      || studyModality === 'DOC';
    const viewerType = forceWebViewer ? 'web' : currentUser.viewer_preference;
    openStudyDirectly(study, viewerType);
  }, [currentUser, openStudyDirectly, showError, t]);

  const handleShowPdfFromTextView = useCallback((reportData) => {
    if (!reportData?.pdf_data) return;
    const tempKey = `pdf_preview_${reportData.report_id}_${Date.now()}`;
    localStorage.setItem(tempKey, JSON.stringify(reportData.pdf_data));
    const width = 950;
    const height = Math.floor(window.screen.availHeight * 0.85);
    const left = Math.floor((window.screen.availWidth / 2 - width) / 2);
    const top = Math.floor((window.screen.availHeight - height) / 2);
    window.open(
      `/pdf-preview?key=${tempKey}`,
      `pdf_preview_${reportData.report_id}`,
      `width=${width},height=${height},left=${left},top=${top},resizable=yes,scrollbars=yes,menubar=no,toolbar=no,location=no,status=no`
    );
  }, []);

  const handleDock = () => {
    postReportSidebarMessage(SYNC_MESSAGE.DOCK, { keepPreference: true });
    window.close();
  };

  const hasContext = Boolean(syncContext?.patientId);

  return (
    <>
      {popupGlobalStyles}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          width: '100%',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            borderRadius: 0,
            flexShrink: 0,
            width: '100%',
          }}
        >
          <Typography variant="subtitle1" sx={{ fontWeight: 600, flex: 1, color: 'primary.main' }}>
            {t('report.sidebar.popupTitle', 'Vorbefunde & KI-Zusammenfassung')}
          </Typography>
          <IconButton size="small" onClick={handleDock} title={t('report.sidebar.dock', 'Andocken')}>
            <DockIcon fontSize="small" sx={{ color: 'var(--rd-primary)' }} />
          </IconButton>
          <IconButton size="small" onClick={() => window.close()} title={t('common.close', 'Schließen')}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Paper>

        {!hasContext ? (
          <Box sx={{ flex: 1, width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
            <Typography variant="body2" color="text.secondary" align="center">
              {t('report.sidebar.empty', 'Kein aktiver Befund geöffnet.')}
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              flex: 1,
              width: '100%',
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box sx={{ flex: aiSummaryExpanded ? '2 1 0' : '1 1 0', width: '100%', minHeight: 0, display: 'flex' }}>
              <PriorResultsCard
              theme={theme}
              fillAvailable
              showPriorReportText={priorStudies.showPriorReportText}
              selectedPriorStudy={priorStudies.selectedPriorStudy}
              onBackFromPriorReportText={priorStudies.handleBackFromPriorReportText}
              onShowPdfFromTextView={handleShowPdfFromTextView}
              priorStudiesSearchText={priorStudies.priorStudiesSearchText}
              onPriorStudiesSearchChange={(e) => priorStudies.setPriorStudiesSearchText(e.target.value)}
              priorStudiesModalityFilter={priorStudies.priorStudiesModalityFilter}
              onPriorStudiesModalityFilterChange={(e) => priorStudies.setPriorStudiesModalityFilter(e.target.value)}
              getUniqueModalities={priorStudies.getUniqueModalities}
              getFilteredAndSortedPriorStudies={priorStudies.getFilteredAndSortedPriorStudies}
              priorStudiesColumns={priorStudies.priorStudiesColumns}
              priorStudiesApiRef={priorStudies.priorStudiesApiRef}
              getGridLocale={priorStudies.getGridLocale}
              onRowDoubleClick={(params) => handleOpenInViewer(params.row)}
              sidebarDetached
              onToggleDetach={handleDock}
              showDetachIcon={false}
            />
            </Box>
            <Box sx={{
              flex: aiSummaryExpanded ? '1 1 0' : '0 0 auto',
              width: '100%',
              minHeight: aiSummaryExpanded ? 0 : undefined,
              display: 'flex',
            }}>
              <AISummaryCard
              fillAvailable
              onExpandedChange={setAiSummaryExpanded}
              patientId={syncContext.patientId}
              examId={syncContext.examId}
              enabled={!!syncContext.patientId}
            />
            </Box>
          </Box>
        )}
      </Box>
    </>
  );
}
